import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthShell from '../components/auth/AuthShell';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore, useWorkspaceStore } from '../store';
import { Eye, EyeOff, ShieldCheck, Check } from 'lucide-react';
import { getApiErrorMessage } from '../lib/api';
import { authService, ProjectInvitationPreview } from '../services/authService';

const AcceptProjectInvite: React.FC = () => {
  const { t } = useTranslation();
  const { acceptProjectInvite, isAuthenticated, user, logout } = useAuthStore();
  const { switchWorkspace } = useWorkspaceStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  // Public preview: decides between the new-account form, log-in-and-join, and
  // the logged-in confirmation. Someone who already has an account (e.g. created
  // their own workspace before clicking the emailed link) can't use the signup
  // form — the backend rightly says "account already exists".
  const [preview, setPreview] = useState<ProjectInvitationPreview | null>(null);
  const [previewError, setPreviewError] = useState('');
  useEffect(() => {
    if (!token) return;
    authService.getProjectInvitationPreview(token)
      .then(setPreview)
      .catch((error) => setPreviewError(getApiErrorMessage(error, t('auth.acceptProjectInviteError'))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [joined, setJoined] = useState<{ project: { id: string; name: string }; workspace: { id: string; name: string } } | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const emailMatches = !!(preview && user && preview.email.toLowerCase() === user.email.toLowerCase());

  const handleJoin = async () => {
    setIsJoining(true);
    setJoinError('');
    try {
      setJoined(await authService.acceptProjectInviteExisting(token));
    } catch (error) {
      setJoinError(getApiErrorMessage(error, t('auth.acceptProjectInviteError')));
    } finally {
      setIsJoining(false);
    }
  };

  const handleSwitchNow = async () => {
    if (!joined) return;
    setIsSwitching(true);
    try {
      // Navigate first: switchWorkspace() reloads the page on success.
      navigate('/dashboard');
      await switchWorkspace(joined.workspace.id);
    } catch {
      setIsSwitching(false);
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name) {
      newErrors.name = t('errors.required');
    }

    if (!formData.password) {
      newErrors.password = t('auth.passwordRequired');
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s]).{8,}$/.test(formData.password)) {
      newErrors.password = t('auth.passwordComplexity');
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('errors.required');
    } else if (formData.confirmPassword !== formData.password) {
      newErrors.confirmPassword = t('auth.passwordMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await acceptProjectInvite(token, formData.name, formData.password);
      navigate('/dashboard');
    } catch (error) {
      console.error('Accept project invite error:', error);
      setErrors({ general: getApiErrorMessage(error, t('auth.acceptProjectInviteError')) });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // The "set your name and password" subtitle only fits the new-account form.
  const showsSignupForm = !!token && !!preview && preview.status === 'pending' && !preview.expired && !isAuthenticated && !preview.account_exists;

  return (
    <AuthShell title={t('auth.acceptProjectInviteTitle')} subtitle={showsSignupForm ? t('auth.acceptProjectInviteSubtitle') : undefined}>

        {!token ? (
          /* Missing token */
          <div className="space-y-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
            <div className="flex items-start gap-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 rounded-xl p-4">
              <ShieldCheck className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
              <p className="text-sm text-red-600 dark:text-red-400">
                {t('auth.acceptProjectInviteMissingToken')}
              </p>
            </div>
            <div className="text-center">
              <Link
                to="/login"
                className="font-medium text-brand-700 hover:text-brand-600"
              >
                {t('auth.backToLogin')}
              </Link>
            </div>
          </div>
        ) : !preview && !previewError ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
          </div>
        ) : previewError || !preview || preview.status !== 'pending' || preview.expired ? (
          <div className="space-y-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
            <div className="flex items-start gap-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 rounded-xl p-4">
              <ShieldCheck className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
              <p className="text-sm text-red-600 dark:text-red-400">
                {previewError || t('auth.acceptProjectInviteUnavailable')}
              </p>
            </div>
            <div className="text-center">
              <Link to="/login" className="font-medium text-brand-700 hover:text-brand-600">
                {t('auth.backToLogin')}
              </Link>
            </div>
          </div>
        ) : isAuthenticated ? (
          /* Logged in: join with one confirmation (no name/password) when the
             account matches the invited email. */
          <div className="space-y-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
            {joined ? (
              <div className="space-y-6">
                <div className="flex items-start gap-4 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/40 rounded-xl p-4">
                  <Check className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-green-700 dark:text-green-300">
                    {t('auth.acceptProjectInviteJoined', { project: joined.project.name })}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleSwitchNow}
                    disabled={isSwitching}
                    className="flex-1 py-2.5 px-4 text-sm font-semibold rounded-xl text-brand-ink bg-brand-400 hover:bg-brand-500 shadow-sm disabled:opacity-50"
                  >
                    {isSwitching ? t('auth.acceptInviteSwitching') : t('auth.acceptInviteSwitchNow')}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="flex-1 py-2.5 px-4 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    {t('auth.acceptInviteStayHere')}
                  </button>
                </div>
              </div>
            ) : emailMatches ? (
              <div className="space-y-6">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('auth.acceptProjectInvitePreview', { project: preview.project_name, workspace: preview.workspace_name })}
                </p>
                {joinError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 rounded-xl p-4">
                    <p className="text-sm text-red-600 dark:text-red-400">{joinError}</p>
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleJoin}
                  disabled={isJoining}
                  className="btn-brand text-sm"
                >
                  {isJoining ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-ink" />
                  ) : (
                    t('auth.acceptProjectInviteJoinButton')
                  )}
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('auth.acceptProjectInviteWrongAccount', { current: user?.email, invited: preview.email })}
                </p>
                <button
                  type="button"
                  onClick={() => { logout(); }}
                  className="w-full py-2.5 px-4 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {t('common.logout')}
                </button>
              </div>
            )}
          </div>
        ) : preview.account_exists ? (
          /* Not logged in but the invited email already has an account: send them
             to log in, and back here afterwards (Login is bounced to ?redirect). */
          <div className="space-y-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t('auth.acceptProjectInvitePreview', { project: preview.project_name, workspace: preview.workspace_name })}
            </p>
            <div className="flex items-start gap-4 bg-brand-50 dark:bg-gray-700/40 border border-brand-200 dark:border-gray-600 rounded-xl p-4">
              <ShieldCheck className="h-5 w-5 text-brand-700 mt-0.5 shrink-0" />
              <p className="text-sm text-gray-700 dark:text-gray-200">{t('auth.acceptProjectInviteLoginPrompt')}</p>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/login?redirect=${encodeURIComponent(`/accept-project-invite?token=${token}`)}`)}
              className="btn-brand text-sm"
            >
              {t('auth.login')}
            </button>
          </div>
        ) : (
          /* Form */
          <form className="space-y-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8" onSubmit={handleSubmit}>
            {errors.general && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 rounded-xl p-4">
                <p className="text-sm text-red-600 dark:text-red-400">{errors.general}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Full Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.fullName')}
                </label>
                <div className="mt-1 relative">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`input-field ${
                      errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                    }`}
                    placeholder="Enter your full name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.password')}
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`input-field pr-10 ${
                      errors.password ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                    }`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.confirmPassword')}
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`input-field pr-10 ${
                      errors.confirmPassword ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                    }`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-brand text-sm"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-ink"></div>
                ) : (
                  <>
                    <ShieldCheck className="h-5 w-5 mr-2" />
                    {t('auth.acceptProjectInviteTitle')}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
    </AuthShell>
  );
};

export default AcceptProjectInvite;